import type { PickerProps } from 'antd/es/date-picker/generatePicker'
import type { RangePickerTimeProps } from 'antd/lib/time-picker'
import type { Moment } from 'moment'
import moment from 'moment'
import React from 'react'
import styled from 'styled-components'

import { RangeValues, utcToTimezoneDate } from '../util/datetime'
import { TestingKey, insertTestingSelector } from '../util/testing'

import { MomentDatePicker } from './DatePicker'
import { DatePickerContainer } from './DatePickerContainer'

export type EventValue<DateType> = DateType | null

export type RangeValue<DateType> = [EventValue<DateType>, EventValue<DateType>] | null

type Props = {
    disabled?: boolean
    label?: string
    errorMessage?: string
    onChange: (date: string) => void
    value?: string | null
    placeholder?: string
    size?: 'large' | 'medium' | 'small'
    disableIcon?: boolean
    hasDanger?: boolean
    disableClear?: boolean
    testingKeys?: [TestingKey] | [TestingKey, string]
    timezone?: string | null | undefined
}

interface MomentTimePickerProps extends Omit<PickerProps<Moment>, 'picker'> {}
interface MomentTimeRangePickerProps extends Omit<RangePickerTimeProps<Moment>, 'picker'> {}

const MomentTimePicker = React.forwardRef<any, MomentTimePickerProps>((props, ref) => (
    <MomentDatePicker {...props} picker="time" ref={ref} />
))

const MomentTimeRangePicker = React.forwardRef<any, MomentTimeRangePickerProps>((props, ref) => (
    <MomentDatePicker.RangePicker {...props} picker="time" ref={ref} />
))

export const TimePicker = (props: Props): JSX.Element => {
    const {
        disabled,
        label,
        errorMessage,
        value,
        onChange,
        placeholder,
        size = 'medium',
        disableIcon,
        hasDanger,
        disableClear,
        testingKeys,
        timezone,
    } = props

    const momentValue = value ? moment(timezone ? utcToTimezoneDate(value, timezone) : value) : null
    const antdSize = size === 'medium' ? 'middle' : size
    const testingSelectors = testingKeys ? insertTestingSelector(...testingKeys) : {}

    return (
        <Container
            errorMessage={errorMessage}
            disabled={Boolean(disabled)}
            label={label}
            disableIcon={disableIcon}
            hasDanger={hasDanger}
        >
            <MomentTimePicker
                {...testingSelectors}
                allowClear={!disableClear}
                disabled={disabled}
                placeholder={placeholder}
                size={antdSize}
                mode="time"
                onChange={(nextValue: Moment) => {
                    if (!nextValue) return
                    onChange(nextValue.toISOString())
                }}
                value={momentValue}
            />
        </Container>
    )
}

const parseValue = (params: {
    isoDatetime?: string | null
    timeOnly?: boolean
}): EventValue<moment.Moment> => {
    const { isoDatetime, timeOnly } = params
    if (!isoDatetime) return null
    if (!timeOnly) return moment(isoDatetime)

    const day = moment().zone('GMT')
    const splitTime = isoDatetime.split(/:/)
    day.hours(parseInt(splitTime[0], 10))
        .minutes(parseInt(splitTime[1], 10))
        .seconds(parseInt(splitTime[2], 10))
        .milliseconds(0)
    return moment(day)
}

export const TimeRangePicker = (
    props: Omit<Props, 'placeholder' | 'onChange' | 'value'> & {
        placeholderStart?: string
        placeholderEnd?: string
        value?: RangeValues
        timeOnly?: boolean
        onChange?: (values: RangeValues) => void
    }
): JSX.Element => {
    const {
        disabled,
        label,
        errorMessage,
        value,
        timeOnly,
        onChange,
        placeholderStart = 'From',
        placeholderEnd = 'End',
        size = 'medium',
        disableIcon,
        hasDanger,
        disableClear,
    } = props

    const antdSize = size === 'medium' ? 'middle' : size
    const startMoment = parseValue({ isoDatetime: value?.start, timeOnly })
    const endMoment = parseValue({ isoDatetime: value?.end, timeOnly })
    const rangeValue: RangeValue<moment.Moment> = [startMoment, endMoment]

    return (
        <Container
            errorMessage={errorMessage}
            disabled={Boolean(disabled)}
            label={label}
            disableIcon={disableIcon}
            hasDanger={hasDanger}
        >
            <MomentTimeRangePicker
                allowClear={!disableClear}
                disabled={disabled}
                placeholder={[placeholderStart, placeholderEnd]}
                size={antdSize}
                onChange={(moments, values) => {
                    if (timeOnly) {
                        const isoDates: RangeValues = {
                            start: values[0],
                            end: values[1],
                        }
                        return onChange?.(isoDates)
                    }
                    const isoDates: RangeValues = {}
                    moments?.forEach((nextMoment, index) => {
                        if (index === 0) {
                            isoDates.start = nextMoment?.toISOString()
                            return
                        }
                        isoDates.end = nextMoment?.toISOString()
                    })

                    return onChange?.(isoDates)
                }}
                value={rangeValue}
            />
        </Container>
    )
}

const Container = styled(DatePickerContainer)``
